import React, { useEffect, useRef, useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { useSettingsMasterContentConfig } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Settings/MasterContentSettingsConfig';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import { useStacklineTheme } from '@stackline/ui';
import IntegrationsActionBox from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Integrations/IntegrationsActionBox';
import { BlankDocumentsIcon, CheckedDocumentsIcon, FileStorageIcon } from 'src/components/SvgIcons';
import axios from 'axios';
import { useSnackbar } from 'src/utils/Hooks';
import { formatError } from 'src/routes/UserAccount/UserAccountUpload/UserAccountUpload';

const DROPDOWN_SKELETON_SIZE = {
  width: 150,
  height: 36
};

const MasterContentSection = () => {
  const theme = useStacklineTheme();
  const {
    description,
    retailersOption,
    isInitialLoading,
    currentMasterContentUrl,
    defaultRetailer,
    defaultStatusUploadName,
    downloadBlankTemplate,
    downloadCurrentMasterContent,
    fetchLastUpdatedFunction,
    fileUploadUrl
  } = useSettingsMasterContentConfig();

  const [selectedRetailerInfo, setSelectedRetailerInfo] = useState(defaultRetailer);
  const [lastUpdatedTime, setLastUpdatedTime] = useState<null | string>(null);
  const fileInputRef = useRef(null);
  const { showSnackbar } = useSnackbar();

  const fetchUpdatedTime = async () => {
    if (selectedRetailerInfo && fetchLastUpdatedFunction) {
      const updatedTimeString = await fetchLastUpdatedFunction({
        retailerId: selectedRetailerInfo.id
      });
      const { uploadedTime, errorMessage } = updatedTimeString;
      if (errorMessage) {
        setLastUpdatedTime("N/A");
      } else {
        setLastUpdatedTime(uploadedTime);
      }
    }
  };

  const handleFileUpload = async (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file, file.name);

      try {
        const urlWithRetailerParam = `${fileUploadUrl}&retailer=${selectedRetailerInfo.id}`;
        const res = await axios.post(urlWithRetailerParam, formData);

        if (res.data === false) {
          return showSnackbar({ type: 'error', message: formatError });
        } else if (res.data) {
          fetchUpdatedTime();
          showSnackbar({ type: 'success', message: 'Upload complete. Your file was successfully uploaded.' });
        } else {
          throw Error('Invalid Response');
        }
      } catch (e) {
        console.error('failed to upload', e);
        showSnackbar({ type: 'error', message: e.response.data });
      }
    }

    return false;
  };

  const handleDownloadCurrentMasterContent = async () => {
    const { errorMessage } = await downloadCurrentMasterContent({
      retailerId: selectedRetailerInfo.id
    });
    if (errorMessage) {
      showSnackbar({
        type: 'error',
        message: errorMessage
      });
    }
  };

  const handleFileUploadButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleDropdownSelect = (retailerInfo) => {
    setSelectedRetailerInfo(retailerInfo);
  };

  useEffect(() => {
    if (!isInitialLoading && retailersOption?.length > 0) {
      setSelectedRetailerInfo(defaultRetailer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialLoading]);

  useEffect(() => {
    fetchUpdatedTime();
  }, [selectedRetailerInfo]);

  return (
    <div>
      <Flex flexDirection="column">
        <Text variant="h4">Master Content</Text>
      </Flex>
      <Flex marginTop="8px">{description}</Flex>
      <Flex marginTop="8px">
        <Text variant="subtitle2">Last Update: </Text> <Text variant="body2">&nbsp; {lastUpdatedTime}</Text>
      </Flex>
      <Flex marginTop={theme.spacing.md} marginBottom={theme.spacing.lg}>
        {selectedRetailerInfo ? (
          <SlDropdownMenu
            defaultLabel={selectedRetailerInfo.label}
            options={retailersOption}
            selectedId={selectedRetailerInfo.id}
            onChange={handleDropdownSelect}
            stacklineButtonProps={{
              textSx: {
                fontWeight: 'normal'
              }
            }}
          />
        ) : (
          <SlSkeleton width={DROPDOWN_SKELETON_SIZE.width} height={DROPDOWN_SKELETON_SIZE.height} />
        )}
      </Flex>
      <IntegrationsActionBox
        icon={<BlankDocumentsIcon />}
        title="Get a template"
        action={{
          description: `Download a blank template to populate ${defaultStatusUploadName}.`,
          descriptionAction: () => downloadBlankTemplate()
        }}
      />

      {currentMasterContentUrl && (
        <IntegrationsActionBox
          icon={<CheckedDocumentsIcon />}
          title={`View ${defaultStatusUploadName}`}
          action={{
            description: `Download the ${defaultStatusUploadName} defined for all SKUs`,
            descriptionAction: () => handleDownloadCurrentMasterContent()
          }}
        />
      )}
      {/* Hidden input for getting upload file */}
      <input
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={(evt) => handleFileUpload(evt)}
        ref={fileInputRef}
      />

      <IntegrationsActionBox
        icon={<FileStorageIcon />}
        title={`Upload ${defaultStatusUploadName}`}
        action={{
          description: `Upload a CSV of your new ${defaultStatusUploadName} for ${
            selectedRetailerInfo && selectedRetailerInfo.label
          }.`,
          descriptionAction: () => handleFileUploadButtonClick()
        }}
      />
    </div>
  );
};

export default MasterContentSection;
